import React, { useState, useEffect } from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/utils/utils';
import { useDateFormatter } from '@/shared/hooks';
import { Button } from '@/shared/components/ui/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/Popover';
import { Label } from '@/shared/components/ui/Label';
import { Calendar } from '@/shared/components/ui/Calendar';

export interface PickedDateRange {
  from: Date;
  to: Date;
}

interface DateRangePickerInputProps {
  value?: PickedDateRange | null;
  onChange: (range: PickedDateRange | null) => void;
  placeholder?: string;
  title?: string;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  inputClassName?: string;
  error?: string;
  format?: 'short' | 'medium' | 'long';
}

export const DateRangePickerInput: React.FC<DateRangePickerInputProps> = ({
  value,
  onChange,
  placeholder,
  title,
  description,
  required = false,
  disabled = false,
  minDate,
  maxDate,
  className = '',
  inputClassName = '',
  error,
  format: dateFormat = 'medium',
}) => {
  const { t } = useTranslation('common');
  const { formatDate } = useDateFormatter();
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(
    value ?? undefined
  );
  const [isOpen, setIsOpen] = useState(false);

  const displayPlaceholder = placeholder || t('pickDateRange');

  useEffect(() => {
    setDraftRange(value ?? undefined);
  }, [value]);

  const handleRangeSelect = (range: DateRange | undefined) => {
    setDraftRange(range);
    if (range?.from && range.to) {
      onChange({ from: range.from, to: range.to });
      setIsOpen(false);
    }
  };

  const clearRange = () => {
    setDraftRange(undefined);
    onChange(null);
    setIsOpen(false);
  };

  const goToToday = () => {
    const today = new Date();
    setDraftRange({ from: today, to: today });
    onChange({ from: today, to: today });
    setIsOpen(false);
  };

  const isDateDisabled = (date: Date) => {
    return (minDate && date < minDate) || (maxDate && date > maxDate) || false;
  };

  const formatRange = (range: PickedDateRange): string =>
    `${formatDate(range.from, dateFormat)} – ${formatDate(
      range.to,
      dateFormat
    )}`;

  return (
    <div className={cn('w-full', className)}>
      {(title || description) && (
        <div className="mb-2 space-y-1">
          {title && (
            <Label className="text-sm font-medium text-foreground">
              {title}
              {required && <span className="text-destructive ms-1">*</span>}
            </Label>
          )}
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>
      )}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className={cn(
              'w-full justify-between text-start font-normal h-10',
              !value && 'text-muted-foreground',
              error &&
                'border-destructive focus:ring-destructive focus:border-destructive',
              inputClassName
            )}
          >
            <span className="truncate">
              {value ? formatRange(value) : displayPlaceholder}
            </span>
            <CalendarIcon className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            min={1}
            selected={draftRange}
            onSelect={handleRangeSelect}
            disabled={isDateDisabled}
            autoFocus
            fixedWeeks
            numberOfMonths={2}
            className="rounded-lg border shadow-sm"
          />
          <div className="flex justify-between gap-2 px-3 pb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={goToToday}
              className="text-sm"
            >
              {t('today')}
            </Button>
            {(value || draftRange?.from) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearRange}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                {t('clear')}
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
    </div>
  );
};
